"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect, useCallback } from "react";
import type { ApiWave } from "@/generated/models/ApiWave";
import { ApiWaveType } from "@/generated/models/ApiWaveType";
import { Time } from "@/helpers/time";
const MAX_PINNED_WAVES = 20;
const STORAGE_KEY = "pinnedWave";
const PINNED_WAVE_SNAPSHOT_TTL_MS = Time.days(1).toMillis();
type PinnedWaveContributor = {
  readonly pfp: string;
  readonly identity?: string | null;
};
export interface PinnedWaveSnapshot {
  readonly id: string;
  readonly name: string | null;
  readonly picture: string | null;
  readonly contributors: readonly PinnedWaveContributor[];
  readonly isDirectMessage: boolean;
  readonly type: ApiWaveType | null;
  readonly fetchedAt: number;
}
interface UpsertPinnedWaveOptions {
  readonly moveToFront?: boolean | undefined;
}
const arePinnedWaveContributorsEqual = (left: readonly PinnedWaveContributor[], right: readonly PinnedWaveContributor[]): boolean => left.length === right.length && left.every((contributor, index) => contributor.pfp === right[index]?.pfp && (contributor.identity ?? null) === (right[index].identity ?? null));
const arePinnedWaveSnapshotsEqual = (left: PinnedWaveSnapshot, right: PinnedWaveSnapshot): boolean => left.id === right.id && left.name === right.name && left.picture === right.picture && left.isDirectMessage === right.isDirectMessage && left.type === right.type && left.fetchedAt === right.fetchedAt && arePinnedWaveContributorsEqual(left.contributors, right.contributors);
const isPinnedWaveSnapshot = (value: unknown): value is PinnedWaveSnapshot & {
  contributors?: unknown;
} => {
  return typeof value === "object" && value !== null && typeof (value as {
    id?: unknown;
  }).id === "string";
};
const normalizePinnedWaveSnapshot = (value: unknown): PinnedWaveSnapshot | null => {
  if (typeof value === "string") {
    return {
      id: value,
      name: null,
      picture: null,
      contributors: [],
      isDirectMessage: false,
      type: null,
      fetchedAt: 0
    };
  }
  if (!isPinnedWaveSnapshot(value)) {
    return null;
  }
  const contributors = Array.isArray(value.contributors) ? value.contributors.filter((contributor): contributor is {
    pfp: string;
    identity?: string | null;
  } => typeof contributor === "object" && contributor !== null && typeof (contributor as {
    pfp?: unknown;
  }).pfp === "string").map(contributor => ({
    pfp: contributor.pfp,
    identity: typeof contributor.identity === "string" ? contributor.identity : null
  })) : [];
  const normalizedType = Object.values(ApiWaveType).includes((value as {
    type?: unknown;
  }).type as ApiWaveType) ? (value as {
    type: ApiWaveType;
  }).type : null;
  const sanitizedFetchedAt = typeof (value as {
    fetchedAt?: unknown;
  }).fetchedAt === "number" && Number.isFinite((value as {
    fetchedAt?: number;
  }).fetchedAt) ? Math.max(0, (value as {
    fetchedAt: number;
  }).fetchedAt) : 0;
  return {
    id: value.id,
    name: typeof value.name === "string" ? value.name : null,
    picture: typeof value.picture === "string" ? value.picture : null,
    contributors,
    isDirectMessage: Boolean(value.isDirectMessage),
    type: normalizedType,
    fetchedAt: normalizedType === null ? 0 : sanitizedFetchedAt
  };
};
const createPinnedWaveSnapshot = (wave: ApiWave, fetchedAt: number = Time.now().toMillis()): PinnedWaveSnapshot => ({
  id: wave.id,
  name: wave.name,
  picture: wave.picture,
  contributors: wave.contributors_overview.filter(contributor => contributor.contributor_pfp.length > 0).map(contributor => ({
    pfp: contributor.contributor_pfp,
    identity: contributor.contributor_identity
  })),
  isDirectMessage: Boolean(wave.chat.scope.group?.is_direct_message),
  type: wave.wave.type,
  fetchedAt
});
export const isPinnedWaveSnapshotStale = (snapshot: PinnedWaveSnapshot, now: number = Time.now().toMillis()): boolean => snapshot.fetchedAt <= 0 || now - snapshot.fetchedAt >= PINNED_WAVE_SNAPSHOT_TTL_MS;
const readPinnedWaves = (): PinnedWaveSnapshot[] => {
  if (typeof window === "undefined") {
    return [];
  }
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) {
    return [];
  }
  try {
    const parsed = JSON.parse(stored) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.map(item => normalizePinnedWaveSnapshot(item)).filter((item): item is PinnedWaveSnapshot => item !== null).slice(0, MAX_PINNED_WAVES);
  } catch {
    return [];
  }
};
export function usePinnedWaves() {
  const $ = _c(14);
  const [pinnedWaves, setPinnedWaves] = useState(_temp);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = (snapshot, t1) => {
      const {
        moveToFront: t2
      } = t1 === undefined ? {} : t1;
      const moveToFront = t2 === undefined ? false : t2;
      setPinnedWaves(prev => {
        const existingIndex = prev.findIndex(pinnedWave => pinnedWave.id === snapshot.id);
        const existingWave = existingIndex === -1 ? null : prev[existingIndex] ?? null;
        if (moveToFront) {
          if (existingIndex === 0 && existingWave && arePinnedWaveSnapshotsEqual(existingWave, snapshot)) {
            return prev;
          }
          const nextWaves = [snapshot, ...prev.filter(pinnedWave_0 => pinnedWave_0.id !== snapshot.id)];
          return nextWaves.slice(0, MAX_PINNED_WAVES);
        }
        if (existingIndex === -1) {
          return [...prev, snapshot].slice(0, MAX_PINNED_WAVES);
        }
        if (existingWave && arePinnedWaveSnapshotsEqual(existingWave, snapshot)) {
          return prev;
        }
        const nextWaves_0 = [...prev];
        nextWaves_0[existingIndex] = snapshot;
        return nextWaves_0;
      });
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const upsertWaveSnapshot = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = (wave, options) => {
      upsertWaveSnapshot(createPinnedWaveSnapshot(wave), options);
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const upsertWave = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = id => {
      setPinnedWaves(prev_0 => prev_0.filter(pinnedWave_1 => pinnedWave_1.id !== id));
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const removeId = t2;
  let t3;
  if ($[3] !== pinnedWaves) {
    t3 = () => pinnedWaves.map(_temp2);
    $[3] = pinnedWaves;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const getAllIds = t3;
  let t4;
  let t5;
  if ($[5] !== pinnedWaves) {
    t4 = () => {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(pinnedWaves));
    };
    t5 = [pinnedWaves];
    $[5] = pinnedWaves;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  let t6;
  if ($[8] !== pinnedWaves) {
    t6 = pinnedWaves.map(_temp3);
    $[8] = pinnedWaves;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== getAllIds || $[11] !== pinnedWaves || $[12] !== t6) {
    t7 = {
      pinnedIds: t6,
      pinnedWaves,
      upsertWave,
      upsertWaveSnapshot,
      removeId,
      getAllIds
    };
    $[10] = getAllIds;
    $[11] = pinnedWaves;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
}
function _temp3(pinnedWave_3) {
  return pinnedWave_3.id;
}
function _temp2(pinnedWave_2) {
  return pinnedWave_2.id;
}
function _temp() {
  return readPinnedWaves();
}